import React from 'react';
import { Box, Typography, Container, Grid, Card, CardContent, CardMedia, Rating, Button } from '@mui/material';
import { styled } from '@mui/material/styles';

const ProductsContainer = styled(Box)(({ theme }) => ({
  padding: '6rem 0',
  backgroundColor: '#f8fafc',
  position: 'relative'
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '3rem',
  fontWeight: 700,
  textAlign: 'center',
  marginBottom: '1rem',
  color: '#1e293b',
  [theme.breakpoints.down('md')]: {
    fontSize: '2.5rem'
  }
}));

const SectionSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.25rem',
  textAlign: 'center',
  marginBottom: '4rem',
  color: '#64748b',
  maxWidth: '600px',
  margin: '0 auto 4rem auto'
}));

const ProductCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderRadius: '24px',
  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.4s ease',
  border: '1px solid #e2e8f0',
  overflow: 'hidden',
  position: 'relative',
  '&:hover': {
    transform: 'translateY(-12px)',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
  }
}));

const ProductImage = styled(CardMedia)(({ theme }) => ({
  height: 240,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.1) 100%)'
  }
}));

const ProductTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.5rem',
  fontWeight: 600,
  marginBottom: '0.5rem',
  color: '#1e293b'
}));

const ProductPrice = styled(Typography)(({ theme }) => ({
  fontSize: '2rem',
  fontWeight: 700,
  color: '#3b82f6',
  marginBottom: '1rem'
}));

const ProductDescription = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  color: '#64748b',
  lineHeight: 1.7,
  marginBottom: '1.5rem'
}));

const ProductButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#3b82f6',
  color: 'white',
  padding: '12px 24px',
  borderRadius: '12px',
  textTransform: 'none',
  fontWeight: 600,
  width: '100%',
  '&:hover': {
    backgroundColor: '#2563eb',
    transform: 'translateY(-2px)'
  }
}));

const RatingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  marginBottom: '1rem'
}));

const ProductsSection = ({ products }) => {
  const defaultProducts = [
    {
      name: 'Premium Business Package',
      price: '$299',
      rating: 4.9,
      image: 'https://c.animaapp.com/mffcpu90bnu9dd/img/ai_4.png',
      description: 'Complete business solution with advanced features and premium support.'
    },
    {
      name: 'Starter Solution',
      price: '$99',
      rating: 4.7,
      image: 'https://c.animaapp.com/mffcpu90bnu9dd/img/ai_3.png',
      description: 'Perfect for small businesses looking to establish their digital presence.'
    },
    {
      name: 'Enterprise Suite',
      price: '$599',
      rating: 5.0,
      image: 'https://c.animaapp.com/mffcpu90bnu9dd/img/ai_1.png',
      description: 'Comprehensive enterprise-level solution with custom integrations.'
    }
  ];

  const displayProducts = products || defaultProducts;

  return (
    <ProductsContainer>
      <Container maxWidth="lg">
        <SectionTitle variant="h2">
          Our Products
        </SectionTitle>
        <SectionSubtitle variant="body1">
          Choose the perfect solution for your business needs
        </SectionSubtitle>
        <Grid container spacing={4}>
          {displayProducts.map((product, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <ProductCard>
                <ProductImage
                  image={product.image}
                  title={product.name}
                />
                <CardContent sx={{ padding: '2rem' }}>
                  <ProductTitle variant="h6">
                    {product.name}
                  </ProductTitle>
                  <ProductPrice variant="h4">
                    {product.price}
                  </ProductPrice>
                  <RatingContainer>
                    <Rating value={product.rating} readOnly size="small" />
                    <Typography variant="body2" color="#64748b">
                      ({product.rating})
                    </Typography>
                  </RatingContainer>
                  <ProductDescription variant="body2">
                    {product.description}
                  </ProductDescription>
                  <ProductButton variant="contained">
                    Choose Plan
                  </ProductButton>
                </CardContent>
              </ProductCard>
            </Grid>
          ))}
        </Grid>
      </Container>
    </ProductsContainer>
  );
};

export default ProductsSection;
